import { useState, useEffect } from 'react'
import './HarptosDatePicker.css'

const MONTHS = [
  'Hammer', 'Alturiak', 'Ches', 'Tarsakh', 'Mirtul', 'Kythorn',
  'Flamerule', 'Eleasis', 'Eleint', 'Marpenoth', 'Uktar', 'Nightal'
]

const SPECIAL_DAYS = [
  { name: 'Midwinter', afterMonth: 'Hammer' },
  { name: 'Greengrass', afterMonth: 'Tarsakh' },
  { name: 'Midsummer', afterMonth: 'Flamerule' },
  { name: 'Shieldmeet', afterMonth: 'Flamerule', leapOnly: true },
  { name: 'Highharvesttide', afterMonth: 'Eleint' },
  { name: 'Feast of the Moon', afterMonth: 'Uktar' }
]

function isLeapYear(year) {
  return year % 4 === 0
}

export default function HarptosDatePicker({ value, onChange, label, disabled = false }) {
  const [dateType, setDateType] = useState('month') // 'month' or 'special'
  const [month, setMonth] = useState('Hammer')
  const [day, setDay] = useState(1)
  const [year, setYear] = useState(1492)
  const [specialDay, setSpecialDay] = useState('Midwinter')

  // Parse incoming value
  useEffect(() => {
    if (!value) return

    // Check if it's a special day
    const special = SPECIAL_DAYS.find(s => value.month === s.name)
    if (special) {
      setDateType('special')
      setSpecialDay(value.month)
      setYear(value.year || 1492)
    } else if (MONTHS.includes(value.month)) {
      setDateType('month')
      setMonth(value.month)
      setDay(value.day || 1)
      setYear(value.year || 1492)
    }
  }, [value])

  const handleChange = (updates) => {
    const newMonth = updates.month ?? (dateType === 'special' ? specialDay : month)
    const newDay = updates.day ?? (dateType === 'special' ? 1 : day)
    const newYear = updates.year ?? year

    onChange({
      month: newMonth,
      day: dateType === 'special' ? 1 : newDay,
      year: newYear
    })
  }

  const handleDateTypeChange = (type) => {
    setDateType(type)
    if (type === 'special') {
      handleChange({ month: specialDay, day: 1 })
    } else {
      handleChange({ month, day })
    }
  }

  const availableSpecialDays = SPECIAL_DAYS.filter(
    s => !s.leapOnly || isLeapYear(year)
  )

  return (
    <div className={`harptos-picker ${disabled ? 'disabled' : ''}`}>
      {label && <label className="harptos-label">{label}</label>}

      <div className="harptos-type-toggle">
        <button
          type="button"
          className={`type-btn ${dateType === 'month' ? 'active' : ''}`}
          onClick={() => handleDateTypeChange('month')}
          disabled={disabled}
        >
          Regular Day
        </button>
        <button
          type="button"
          className={`type-btn ${dateType === 'special' ? 'active' : ''}`}
          onClick={() => handleDateTypeChange('special')}
          disabled={disabled}
        >
          Festival/Holiday
        </button>
      </div>

      <div className="harptos-fields">
        {dateType === 'month' ? (
          <>
            <div className="harptos-field">
              <label>Month</label>
              <select
                value={month}
                onChange={(e) => {
                  setMonth(e.target.value)
                  handleChange({ month: e.target.value })
                }}
                disabled={disabled}
              >
                {MONTHS.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="harptos-field harptos-day">
              <label>Day</label>
              <input
                type="number"
                min="1"
                max="30"
                value={day}
                onChange={(e) => {
                  const d = Math.min(30, Math.max(1, parseInt(e.target.value) || 1))
                  setDay(d)
                  handleChange({ day: d })
                }}
                disabled={disabled}
              />
            </div>
          </>
        ) : (
          <div className="harptos-field harptos-special">
            <label>Holiday</label>
            <select
              value={specialDay}
              onChange={(e) => {
                setSpecialDay(e.target.value)
                handleChange({ month: e.target.value })
              }}
              disabled={disabled}
            >
              {availableSpecialDays.map(s => (
                <option key={s.name} value={s.name}>{s.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="harptos-field harptos-year">
          <label>Year (DR)</label>
          <input
            type="number"
            value={year}
            onChange={(e) => {
              const y = parseInt(e.target.value) || 1492
              setYear(y)
              handleChange({ year: y })
            }}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  )
}
